import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, CheckCircle2, Circle } from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { Section } from "@/components/dashboard/DashboardPrimitives";
import { downloadBlob } from "@/components/quiz/quizUtils";

const Use = ({ used, at, amount, left, testId }) => (
  <div className="text-xs" data-testid={testId}>
    <span className={`inline-flex items-center gap-1 ${used ? "text-teal-300" : "text-slate-500"}`}>
      {used ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {used ? "Used" : "Not used"}
    </span>
    {used && <div className="text-slate-500">{at ? new Date(at).toLocaleDateString() : "–"} · {amount}{left ? ` · ${left} credit left` : " · report made"}</div>}
  </div>
);

export default function PersonalCodesPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/api/quiz/admin/codes").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.items || []).filter((r) => !t || [r.code, r.owner_name, r.owner_email, r.owner_phone].some((v) => (v || "").toLowerCase().includes(t)));
  }, [data, q]);

  const exportXlsx = () => downloadBlob("/api/quiz/admin/codes/export", "tots360_personal_codes.xlsx").catch((e) => toast.error(formatApiError(e)));

  return (
    <Section title="Personal codes" description={data ? `${data.total} codes · ${data.growth_used} used for Growth · ${data.nutrition_used} used for NutriGuide` : "Codes given to parents after the quiz."}
      testid="admin-codes-section"
      right={<button onClick={exportXlsx} className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1.5" data-testid="admin-codes-export-btn"><Download className="w-4 h-4" /> Excel</button>}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name, email or phone"
        className="w-full mb-3 px-3 py-2 text-sm rounded bg-[#0E1A3A] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none" data-testid="admin-codes-search-input" />
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="admin-codes-table">
          <thead className="text-xs text-slate-400 text-left">
            <tr className="border-b border-slate-800">{["Code", "Owner", "Created", "Growth Assessment", "NIN-NutriGuide"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-slate-400">No codes generated yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-slate-800/70 last:border-0" data-testid={`admin-code-row-${r.code}`}>
                <td className="px-4 py-2 font-mono text-white">{r.code}</td>
                <td className="px-4 py-2"><div className="text-slate-100">{r.owner_name}</div><div className="text-xs text-slate-400">{r.owner_email}{r.owner_phone ? ` · ${r.owner_phone}` : ""}</div></td>
                <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "–"}</td>
                <td className="px-4 py-2"><Use used={r.growth_used} at={r.growth_used_at} amount={r.growth_amount} left={r.growth_credit_left} testId={`admin-code-growth-${r.code}`} /></td>
                <td className="px-4 py-2"><Use used={r.nutrition_used} at={r.nutrition_used_at} amount={r.nutrition_amount} left={r.nutrition_credit_left} testId={`admin-code-nutrition-${r.code}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
